import React, {useCallback, useEffect, useMemo, HTMLAttributes, useState, FocusEvent} from 'react';
import {Icon, IconType} from '../Icon';
import {SelectInputitem, InputStateTypes} from '@/interface';
import {Text} from '../Text';
import {Checkbox} from '../Checkbox';
import {cn} from '@/utils/common_functions';
import {useClickOutside} from '@/hooks';
import {X} from 'lucide-react';

interface UIMultiSelectInputProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onChange'> {
  value: SelectInputitem['id'][] | null; // Changed to array
  label?: string;
  name?: string;
  placeholder?: string;
  options: SelectInputitem[];
  onChange?: (items: SelectInputitem[]) => void; // Changed to return array
  required?: boolean;
  error?: string;
  touched?: boolean;
  className?: string;
  dropDownClassname?: string;
  wrapperClassName?: string;
  itemClassName?: string;
  itemTextClassName?: string;
  disabled?: boolean;
  leftIcon?: IconType;
  iconClassName?: string;
  labelClassName?: string;
  textClassName?: string;
  badgeClassName?: string;
  badgeContainerClassName?: string;
  lockedValues?: SelectInputitem['id'][];
}

export const MultiSelectInput: React.FC<UIMultiSelectInputProps> = props => {
  const {
    value = null,
    className = '',
    dropDownClassname = '',
    error = '',
    itemClassName = '',
    itemTextClassName = '',
    label = '',
    name = '',
    onBlur,
    onFocus,
    onChange,
    options = [],
    placeholder = '',
    required = false,
    touched = false,
    disabled = false,
    wrapperClassName,
    labelClassName = '',
    textClassName = '',
    leftIcon = '',
    iconClassName = '',
    badgeClassName = '',
    badgeContainerClassName = '',
    lockedValues = [],
  } = props;

  /**
   * state to handle the dropdown open state
   */
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [inputState, setInputState] = useState<InputStateTypes>('default');

  // utility function to handle click outside of the component
  const ref = useClickOutside<HTMLButtonElement>(() => {
    setDropdownOpen(false);
    updateInputState(error && touched ? 'error' : 'default');
  });

  /**
   * utility function to get the selected items
   */
  const selectedItems = useMemo<SelectInputitem[]>(() => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return [];
    }
    return options.filter(option => value.includes(option.id));
  }, [value, options]);

  const textContent = useMemo(() => {
    if (selectedItems.length === 0) {
      return '';
    }
    if (selectedItems.length === 1) {
      return selectedItems[0].label;
    }
    return `${selectedItems.length} selected`;
  }, [selectedItems]);

  /**
   * utility function to update input state with a check to disabled logic,
   */
  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  function onFocusHandler(e: FocusEvent<HTMLButtonElement>): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  function onBlurHandler(e: FocusEvent<HTMLButtonElement>): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  const handleSelect = (option: SelectInputitem) => {
    const currentValues = value || [];
    let newSelectedItems: SelectInputitem[];
    if (lockedValues.includes(option.id)) {
      return;
    }

    if (currentValues.includes(option.id)) {
      // Remove if already selected
      newSelectedItems = selectedItems.filter(item => item.id !== option.id);
    } else {
      // Add if not selected
      newSelectedItems = [...selectedItems, option];
    }

    onChange?.(newSelectedItems);
  };

  const handleRemoveBadge = (e: React.MouseEvent, optionId: SelectInputitem['id']) => {
    e.stopPropagation();
    const newSelectedItems = selectedItems.filter(item => item.id !== optionId);
    onChange?.(newSelectedItems);
  };

  const isSelected = (optionId: SelectInputitem['id']) => {
    return value?.includes(optionId) || false;
  };

  /**
   * Update input state on error or touched
   */
  useEffect(() => {
    if (touched && error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, touched, updateInputState]);

  return (
    <div className={cn('flex flex-col gap-2 w-full', className)}>
      {label && (
        <Text variant="body" className={cn('!font-FigtreeSemiBold inline-flex text-ui_blue gap-1', labelClassName)}>
          {label}
          {required && <p className="text-rejection">*</p>}
        </Text>
      )}

      <button
        ref={ref}
        onClick={() => setDropdownOpen(p => !p)}
        disabled={disabled}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
        className={cn(
          'border-2 flex px-3 py-2 lg:px-4 border-ui_input_border relative rounded-lg items-center disabled:cursor-not-allowed w-full',
          inputState === 'error' && 'border-rejection',
          inputState === 'focus' && 'border-black',
          inputState === 'disabled' && 'border-x-ui_disabled',
          wrapperClassName,
        )}>
        {leftIcon && <Icon type={leftIcon} className={cn('size-4', iconClassName)} />}
        <Text
          className={cn(
            'grow text-start font-FigtreeRegular text-base max-w-full overflow-hidden text-ellipsis whitespace-nowrap',
            !textContent && 'text-ui_input_border_dark',
            textClassName,
          )}>
          {textContent || placeholder}
        </Text>
        <Icon type={'cheveron_down'} className={cn('size-4')} />

        {/* Dropdown */}
        {dropdownOpen && (
          <div
            className={cn(
              'flex border max-h-[200px] overflow-y-auto overflow-x-hidden w-full border-ui_input_border z-[999] rounded-lg bg-white flex-col absolute top-[104%] right-0 left-0',
              dropDownClassname,
            )}>
            {options.map(item => (
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleSelect(item);
                }}
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(item);
                  }
                }}
                className={cn(
                  'hover:bg-ui_sky_blue px-3 py-2 border-b border-ui_input_border last:border-b-0 cursor-pointer flex items-center gap-2',
                  isSelected(item.id) && 'bg-ui_sky_blue',
                  itemClassName,
                )}
                key={item.id}>
                {/* Checkbox indicator */}
                <Checkbox
                  value={Boolean(item.id)}
                  onChange={() => handleSelect(item)}
                  checked={isSelected(item.id)}
                  disabled={disabled || lockedValues.includes(item.id)}
                />
                <Text className={cn('text-start font-FigtreeMeduim text-black text-base', isSelected(item.id) && 'font-FigtreeBold', itemTextClassName)}>
                  {item.label}
                </Text>
              </button>
            ))}
          </div>
        )}
      </button>

      {/* Selected badges */}
      {selectedItems.length > 0 && (
        <div className={cn('flex flex-wrap gap-2', badgeContainerClassName)}>
          {selectedItems.map(item => (
            <div
              key={item.id}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 bg-white border border-ui_blue rounded-full',
                badgeClassName,
                lockedValues.includes(item.id) && 'opacity-50 cursor-not-allowed',
              )}>
              <Text className="text-sm font-FigtreeMeduim text-ui_blue">{item.label}</Text>
              {!lockedValues.includes(item.id) && (
                <button onClick={e => handleRemoveBadge(e, item.id)} className="hover:bg-gray-200 rounded-full p-0.5 transition-colors" type="button">
                  <X className="size-4 text-gray-600" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {touched && error && <Text className="text-rejection text-sm">{error}</Text>}
    </div>
  );
};
